import { z } from "zod";
import { useCallback, useEffect, useMemo } from "react";
import { useParams } from "@tanstack/react-router";
import { useCommittedLocation } from "@/routing/committedLocation";
import {
  useSameRouteNavigate,
  type SameRouteUpdate,
  type UrlRecord,
} from "@/routing/sameRoute";
import countryList from "@/components/data/CountryList";
import residentialCountryCodes from "@/components/data/ResidentialCountryCodes";
import { PlanIdTypeSafe } from "@/types/PlanTypes";

/**
 * zod schema
 * --------------
 * zod keeps us safe from typos in the url bar.
 * keys are the query param keys
 * values are the zod schemas for the values
 *
 * Why are types stored in this file and not defined in pages or components?
 * --------------
 * The URL is a globally accessible to the app, therefore they should be
 * defined in a single place: here.
 */
const OptionSchema = z.object({
  id: z.string(),
  label: z.string(),
  subtitle: z.string(),
  available: z.number(),
});
const SingleOption = OptionSchema.optional();
const SingleOrPluralMultiSelectSchema = z
  .union([OptionSchema, z.array(OptionSchema)])
  .optional();
const UrlSchema = z.object({
  // Replace Proxy Page
  // ---------
  mode: z.string().optional(),
  addType: z
    .union([
      z.literal("any"),
      z.literal("country"),
      z.literal("asn"),
      z.literal("ip_range"),
    ])
    .optional(),
  removeType: z.union([
    z.literal("any"),
    z.literal("country"),
    z.literal("asn"),
    z.literal("ip_range"),
    z.literal("refresh_all"),
  ]),
  replaceProxyStep: z.union([z.literal(0), z.literal(1), z.literal(2)]),
  removeAsn: SingleOption,
  removeCountry: SingleOption,
  removeRange: SingleOption,
  verificationCategory: z.string(),
  removeIpsText: z.string().optional(),
  addAsns: SingleOrPluralMultiSelectSchema,
  addCountries: SingleOrPluralMultiSelectSchema,
  addRanges: SingleOrPluralMultiSelectSchema,
  // Activity Page
  // ---------
  activityTimestampLte: z.string().datetime({ precision: 3 }).optional(), // "2020-01-01T00:00:00.123Z"
  activityTimestampGte: z.string().datetime({ precision: 3 }).optional(), // "2020-01-01T00:00:00.123Z"
  // List Page
  // ---------
  authenticationMethod: z
    .union([z.literal("username_password"), z.literal("ip")])
    .optional(),
  // Endpoint Generator Page
  // ---------
  endpointAuthMethod: z
    .union([
      z.literal("default"),
      z.literal("subuser"),
      z.literal("authorized_ip"),
    ])
    .optional(),
  endpointSessionType: z
    .union([z.literal("rotating"), z.literal("sticky")])
    .optional(),
  endpointAuthorizedIp: z.string().optional(),
  endpointCountries: z.array(z.enum(residentialCountryCodes)).optional(),
  endpointCity: z.string().optional(),
  endpointState: z.string().optional(),
  endpointZip: z.string().optional(),
  endpointAsn: z.string().optional(),
  connectionMethod: z
    .union([z.literal("direct"), z.literal("backbone"), z.literal("rotating")])
    .optional(),
  proxyControl: z.string().optional(),
  filterByCountryOpen: z.boolean().optional(),
  exampleCodeOpen: z.boolean().optional(),
  planId: z
    .union([z.number(), z.literal("default")])
    .transform((v) => (v !== undefined ? v : "default") as PlanIdTypeSafe)
    .optional(),
  // Customize
  // ---------
  isCustomPlan: z.boolean().optional(),
  // customPlanType: z.union([z.literal("free")]),
  customPlanType: z.literal("verified_custom").optional(),
  proxyType: z
    .union([
      z.literal("free"),
      z.literal("shared"),
      z.literal("semidedicated"),
      z.literal("dedicated"),
    ])
    .optional()
    .default("shared"),
  proxySubtype: z
    .union([
      z.literal("default"),
      z.literal("non-premium"),
      z.literal("premium"),
      z.literal("isp"),
      z.literal("residential"),
      z.literal("verified"),
      z.literal("datacenter_and_isp"),
    ])
    .default("default"),

  proxyCount: z.number().default(100),
  customProxyCount: z.number().default(0),
  autoRefreshFrequency: z.number().default(0),
  isCustomAutoRefreshFrequency: z.boolean().default(false),
  isUnlimitedIpAuthorizations: z.boolean().default(false),
  isHighConcurrency: z.boolean().default(false),
  isHighPriorityNetwork: z.boolean().default(false),
  isHighIpReputation: z.boolean().default(false),
  isYearly: z.boolean().default(false),
  is2xProxies: z.boolean().default(false),
  addonPlan: z.string().optional(),

  isCustomSubuserCount: z.boolean().default(false),
  isCustomOnDemandRefreshes: z.boolean().default(false),
  proxyReplacements: z.number().default(10),
  isCustomProxyReplacements: z.boolean().default(false),
  onDemandRefreshes: z.number().default(0),
  subuserCount: z.number().default(3),
  customSubuserCount: z.number().default(0),
  bandwidth: z.number().default(5000),
  isCustomBandwidth: z.boolean().default(false),
  siteChecks: z.string().array().default([]),
  rowsPerPage: z.number().optional(),
  page: z.number().optional(),
  order: z.union([z.literal("asc"), z.literal("desc")]).optional(),
  orderBy: z.string().nullable().optional(),
  searchValue: z.string().optional(),
  filters: z
    .object({
      id: z.union([z.undefined(), z.string(), z.number()]),
      field: z.string(),
      value: z.any(),
      operator: z.string(),
    })
    .array()
    .optional(),
  customAutomaticRefreshFrequencyPeriod: z
    .union([z.literal("h"), z.literal("min"), z.literal("w"), z.literal("mo")])
    .default("h"),
  countries: z
    .record(
      z.enum(Object.keys(countryList) as [keyof typeof countryList]),
      z.union([z.number(), z.undefined()])
    )
    .default({}),

  // Express Checkout
  showPlanDetails: z.boolean().optional(),
  // Quick Start onboarding modal (shown once right after registration)
  showQuickStart: z.boolean().optional(),
  source: z
    .union([
      z.literal("resi-hero-create-account"),
      z.literal("browse_plan_iframe"),
      z.string(),
    ])
    .optional(),
  // Subscription Page
  openPaymentMethod: z.boolean().optional(),
  // Cancel Subscription Page
  planIds: z
    .number()
    .array()
    .transform((v) => (v !== undefined ? (v as PlanIdTypeSafe[]) : undefined))
    .optional(),
});

export type UrlVal = z.infer<typeof UrlSchema>;
export type UrlValOption = z.infer<typeof OptionSchema>;
export type SetParam<T> = (value: T) => void;

function zodParse(key: keyof UrlVal, value: any) {
  return UrlSchema.shape[key].parse(value);
}

function zodSafeParse(key: keyof UrlVal, value: unknown) {
  return UrlSchema.shape[key].safeParse(value);
}

/**
 * The URL as one record, which is what Next's `router.query` was: the query
 * string with the path segments layered on top.
 *
 * The merge is load-bearing rather than incidental. `planId` is a key of
 * `UrlSchema` *and* a path segment on every `/$planId/*` route — a query
 * parameter everywhere else — so `usePlanIdUrlState` resolves through it, and
 * the path has to win exactly as it did under Next (ANO-120).
 */
function useUrlRecord(): UrlRecord {
  const search = useSearchRecord();
  const pathParams = usePathParams();
  return useMemo(() => ({ ...search, ...pathParams }), [pathParams, search]);
}

/**
 * The query string, read from the location rather than from the route match.
 *
 * The distinction is the whole reason this hook exists. `useSearch` resolves
 * through the match, and a match only republishes its `search` once
 * `router.load()` has rebuilt the match set — a whole navigation later. Every
 * read here feeds a *write* guard ("is this key already in the URL?"), and a
 * guard that cannot see the write it just issued does not suppress the next
 * one: the seeding effect in `useBatchUrlState` re-fires on the following
 * render, navigates to the identical URL, and TanStack answers a same-URL
 * commit with `router.load()` — which re-renders, which re-fires the effect.
 * That livelock ends in React's "maximum update depth exceeded", which is what
 * `@SCN-049`, `@SCN-020` and `@SMK-025` caught.
 *
 * Next had no such gap: `router.query` was one live object, and the code that
 * used to live here mutated it in place before pushing precisely so the next
 * render's guard saw the pending write. `useCommittedLocation` is the native
 * equivalent of that timing — it adopts a same-path search change immediately,
 * and holds the old location across a path change exactly as Next did
 * (ANO-079).
 */
function useSearchRecord(): UrlRecord {
  return useCommittedLocation().search as UrlRecord;
}

/** The current route's path segments, keyed by segment name. */
function usePathParams(): UrlRecord {
  return useParams({ strict: false }) as UrlRecord;
}

/**
 * Splits a set of writes the way Next's URL interpolation did: a key that names
 * a path segment of the current route goes into the path, everything else into
 * the query string.
 *
 * Getting this wrong is silent. `planId` is a path segment on `/$planId/*`;
 * Next took it out of the query object and interpolated it into the pattern, so
 * treating `UrlVal` as pure search would drop the plan out of the path and
 * leave the app unscoped with typecheck, lint and Jest all still green
 * (ANO-120). The same rule explains the deletion below: any path-segment key
 * that also appears in the query string was consumed by that interpolation and
 * so never survived a push.
 *
 * Both halves are updaters, which is the part Next could not do — see
 * `useSameRouteNavigate`.
 */
function toSameRouteUpdate(
  updates: UrlRecord,
  pathParams: UrlRecord,
  method?: "replace" | "push"
): SameRouteUpdate {
  const pathKeys = Object.keys(pathParams);
  const pathUpdates: UrlRecord = {};
  const searchUpdates: UrlRecord = {};
  for (const [key, value] of Object.entries(updates)) {
    if (pathKeys.includes(key)) pathUpdates[key] = value;
    else searchUpdates[key] = value;
  }

  return {
    replace: method === "replace",
    search: (previous) =>
      withoutPathKeys({ ...previous, ...searchUpdates }, pathParams),
    ...(Object.keys(pathUpdates).length > 0
      ? { params: (previous: UrlRecord) => ({ ...previous, ...pathUpdates }) }
      : {}),
  };
}

/**
 * A path-segment key that also sits in the query string was consumed by Next's
 * interpolation, so it never survived a push. Dropping it keeps a hand-typed
 * `/36/proxy/list?planId=99` resolving to one plan rather than two.
 */
function withoutPathKeys(search: UrlRecord, pathParams: UrlRecord): UrlRecord {
  for (const key of Object.keys(pathParams)) delete search[key];
  return search;
}

/**
 * useDeleteQueryParam
 * --------------
 * Deletes many query params at once
 *
 * This function takes an array of keys and deletes all of those query params
 * from the URL in one go, so that a screen clearing several at once does not
 * have to sequence the navigations itself.
 *
 * @example
 * const deleteQueryParams = useDeleteQueryParams();
 * deleteQueryParams(["mode", "addType", "removeType"]);
 */
export function useDeleteQueryParams() {
  const search = useSearchRecord();
  const pathParams = usePathParams();
  const navigateSameRoute = useSameRouteNavigate();

  // Takes an array of keys to delete from the url
  return useCallback(
    (keys: (keyof UrlVal)[], method?: "replace" | "push") => {
      // Skip if none of the keys are in the query string. A path segment is not
      // a query param and cannot be deleted — Next threw trying — so the merged
      // record is deliberately not consulted here.
      const hasKeysToDelete = keys.some((key) => search[key] !== undefined);
      if (!hasKeysToDelete) return;

      navigateSameRoute({
        replace: method === "replace",
        search: (previous) => {
          const next = { ...previous };
          keys.forEach((key) => {
            delete next[key];
          });
          return withoutPathKeys(next, pathParams);
        },
      });
    },
    [navigateSameRoute, pathParams, search]
  );
}

// Setter, this sets the key=value pair in the url
function useSetQueryParam<T>(
  parameterKey: keyof UrlVal,
  method: "replace" | "push"
) {
  const url = useUrlRecord();
  const pathParams = usePathParams();
  const navigateSameRoute = useSameRouteNavigate();

  return useCallback(
    (value: T) => {
      try {
        zodParse(parameterKey, value);
      } catch (e) {
        throw new Error(`Invalid value for '${parameterKey}': ${e}`);
      }

      // Convert the value to a string
      const paramValue = JSON.stringify(value);

      // Skip navigation if the value hasn't changed.
      if (url[parameterKey] === paramValue) return;

      navigateSameRoute(
        toSameRouteUpdate({ [parameterKey]: paramValue }, pathParams, method)
      );
    },
    [url, parameterKey, pathParams, method, navigateSameRoute]
  );
}

/**
 * Initializer (optional)
 * --------------
 * This sets the initial value of the key=value pair in the url if one is
 * provided.
 *
 * const [addType, setAddType] = useUrlState("addType", "any");
 *
 * In this case "any" would be set in the URL when the page loads if there is no
 * existing value in the URL.
 */
function useInitialQueryParam<T>(
  setParam: SetParam<T>,
  initialValue: T | undefined,
  query: UrlRecord,
  parameterKey: keyof UrlVal
) {
  useEffect(() => {
    if (query[parameterKey] !== undefined || initialValue === undefined) {
      return;
    }
    // Validate initial value with Zod
    try {
      zodParse(parameterKey, initialValue);
    } catch (e) {
      throw new Error(`Invalid value for '${parameterKey}': ${e}`);
    }

    setParam(initialValue);
  }, [setParam, initialValue, query, parameterKey]);
}

function parseValue<K extends keyof UrlVal>(
  key: K,
  query: UrlRecord,
  initialValue?: UrlVal[K]
) {
  const value = query[key];

  if (value) {
    // Deserialize: try JSON.parse first, fall back to raw string
    // (raw strings come from external sources, e.g. ?source=home_hero_button)
    let deserialized: unknown;
    try {
      deserialized = JSON.parse(value as string);
    } catch {
      deserialized = value as string;
    }

    // Validate with Zod — if the URL value is invalid, fall back to
    // schema default (e.g. proxyType → "shared") or initialValue.
    // Uses safeParse to avoid throwing ZodErrors (PROXYCONTROLPANELDASHBOARD-6CD).
    const result = zodSafeParse(key, deserialized);
    if (result.success) return result.data as UrlVal[K];

    // URL value was invalid — try schema default (e.g. proxyType → "shared")
    const defaultResult = zodSafeParse(key, undefined);
    if (defaultResult.success && defaultResult.data !== undefined) {
      return defaultResult.data as UrlVal[K];
    }

    return initialValue;
  }

  if (initialValue !== undefined) {
    // Optimistically return the initial value if there is no value in the URL
    const result = zodSafeParse(key, initialValue);
    if (result.success) return result.data as UrlVal[K];
    // We don't throw an error here because useInitialQueryParam will throw
  }

  return undefined;
}

/**
 * useUrlState
 * --------------
 * const [addType, setAddType] = useUrlState("addType", "any");
 * addType is the value of the key=value pair in the url
 * setAddType is a function that sets the value of the key=value pair in the url
 *
 * IMPORTANT:
 * The value returned from this hook is validated with Zod. If the value in the
 * url is invalid, an error will be thrown.
 */

export default function useUrlState<K extends keyof UrlVal>(
  key: K,
  initialValue?: UrlVal[K],
  method: "push" | "replace" = "push"
): [UrlVal[K] | undefined, SetParam<UrlVal[K]>] {
  const query = useUrlRecord();
  const setParam = useSetQueryParam<UrlVal[K]>(key, method);

  // Initialize parameter with the initial value
  useInitialQueryParam(setParam, initialValue, query, key);
  const parsedValue = parseValue(key, query, initialValue);
  return [parsedValue, setParam];
}

/**
 * useBatchUrlState
 * --------------
  const [values, setParams] = useBatchUrlState([
    ["addType", "any"],
    ["removeType", "country"],
  ])
 * 
 * setParams will update the key value pairs set from the batch update
 *
  setParams([
    ["addType", "any"],
    ["removeType", "country"],
  ])
 */

export function useBatchUrlState<K extends keyof UrlVal>(
  keyValuePairs: [key: K, value?: UrlVal[K]][],
  method?: "replace" | "push"
): [(UrlVal[K] | undefined)[], SetParam<[K, UrlVal[K]][]>] {
  const query = useUrlRecord();
  const pathParams = usePathParams();
  const navigateSameRoute = useSameRouteNavigate();
  const setParams = useCallback(
    (keyValuePairs: [key: K, value?: UrlVal[K]][]) => {
      const updateQuery = keyValuePairs.reduce((acc, [key, value]) => {
        try {
          zodParse(key, value);
          Object.assign(acc, { [key]: JSON.stringify(value) });
          return acc;
        } catch (e) {
          throw new Error(`Invalid value for '${key}': ${e}`);
        }
      }, {} as UrlRecord);

      // Skip navigation if the URL wouldn't actually change
      const hasChange = Object.keys(updateQuery).some(
        (key) => query[key] !== updateQuery[key]
      );
      if (!hasChange) return;

      navigateSameRoute(toSameRouteUpdate(updateQuery, pathParams, method));
    },
    [method, navigateSameRoute, pathParams, query]
  );

  useEffect(() => {
    const initialParams = keyValuePairs.filter(([key, value]) => {
      return query[key] === undefined && value !== undefined;
    });
    initialParams.length && setParams(initialParams);
  }, [keyValuePairs, query, setParams]);

  const parsedValues = keyValuePairs.map(([key, value]) =>
    parseValue(key, query, value)
  );

  return [parsedValues, setParams];
}

/**
 * Takes an object, and returns a new object where all values were run through JSON.stringify.
 *
 * Useful when you want to write a new query object to the URL. eg
 * navigate({ to: "/path", search: prepareObjectForQuery({ key: [1,2,3] }) })
 */
export function prepareObjectForQuery(
  obj: Partial<UrlVal>
): Record<string, string> {
  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      return { ...acc, [key]: JSON.stringify(value) };
    },
    {} as Record<string, string>
  );
}
